import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type FC,
  type ChangeEvent,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { ChevronDown, X, Loader2 } from "lucide-react";

// === File-local companions ============================================

interface AsyncOption {
  id: string;
  label: string;
}

type LabelsCache = Map<string, string>;

type SearchFn = (query: string) => Promise<AsyncOption[]>;

interface AsyncMultiSelectDropdownProps {
  label?: string;
  selectedValues?: string[];
  onChange?: (ids: string[]) => void;
  onSearch?: SearchFn;
  hasActiveFilter?: boolean;
  debounceMs?: number;
  minChars?: number;
  placeholder?: string;
  selectedLabels?: LabelsCache;
}

/**
 * AsyncMultiSelectDropdown - Multi-select dropdown com busca server-side (lazy loading)
 *
 * Diferente do MultiSelectDropdown que recebe options prontas, este componente
 * faz busca no servidor quando o usuário digita.
 *
 * Props:
 * - label: string - Label do dropdown
 * - selectedValues: string[] - IDs selecionados
 * - onChange: (ids: string[]) => void - Callback ao mudar seleção
 * - onSearch: (query: string) => Promise<Array<{id, label}>> - Função de busca server-side
 * - hasActiveFilter?: boolean - Se tem filtro ativo (para estilo)
 * - debounceMs?: number - Tempo de debounce em ms (default: 300)
 * - minChars?: number - Mínimo de caracteres para buscar (default: 0)
 * - placeholder?: string - Placeholder quando nada selecionado
 * - selectedLabels?: Map<string, string> - Cache de labels para IDs selecionados
 */
const AsyncMultiSelectDropdown: FC<AsyncMultiSelectDropdownProps> = ({
  label,
  selectedValues = [],
  onChange,
  onSearch,
  hasActiveFilter = false,
  debounceMs = 300,
  minChars = 0,
  placeholder = "Todos",
  selectedLabels,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [options, setOptions] = useState<AsyncOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  // Cache local de labels para itens selecionados
  const [labelsCache, setLabelsCache] = useState<LabelsCache>(
    () => new Map<string, string>(selectedLabels ?? []),
  );

  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: globalThis.MouseEvent) => {
      // DOM boundary: event.target is EventTarget | null; narrow to Node for contains()
      const target = event.target;
      if (
        dropdownRef.current &&
        target instanceof Node &&
        !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Busca com debounce
  const debouncedSearch = useCallback<SearchFn>(async (query) => {
    if (query.length < minChars && query.length > 0) {
      setOptions([]);
      setLoading(false);
      return [];
    }

    setLoading(true);
    try {
      const results = onSearch ? await onSearch(query) : [];
      setOptions(results || []);
      setHasSearched(true);

      // Atualizar cache de labels com os resultados
      if (results) {
        setLabelsCache(prev => {
          const newCache: LabelsCache = new Map(prev);
          results.forEach((r: AsyncOption) => {
            if (r.id && r.label) {
              newCache.set(r.id, r.label);
            }
          });
          return newCache;
        });
      }
      return results || [];
    } catch (error) {
      console.error('[AsyncMultiSelectDropdown] Erro na busca:', error);
      setOptions([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [onSearch, minChars]);

  // Effect para debounce da busca
  useEffect(() => {
    if (!isOpen) return;

    // Limpar timeout anterior
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Se a query está vazia e minChars é 0, buscar imediatamente (lista inicial)
    if (searchTerm.trim() === "" && minChars === 0) {
      debouncedSearch("");
      return;
    }

    // Debounce para queries não vazias
    debounceRef.current = setTimeout(() => {
      debouncedSearch(searchTerm.trim());
    }, debounceMs);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchTerm, isOpen, debouncedSearch, debounceMs, minChars]);

  // Carregar opções iniciais quando abre (se minChars = 0)
  useEffect(() => {
    if (isOpen && minChars === 0 && !hasSearched && options.length === 0) {
      debouncedSearch("");
    }
  }, [isOpen, minChars, hasSearched, options.length, debouncedSearch]);

  // Reset ao fechar
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setOptions([]);
      setHasSearched(false);
    }
  }, [isOpen]);

  const toggleOption = (optionId: string, optionLabel: string | null) => {
    const newSelected = selectedValues.includes(optionId)
      ? selectedValues.filter(id => id !== optionId)
      : [...selectedValues, optionId];

    // Atualizar cache de labels
    if (optionLabel && !labelsCache.has(optionId)) {
      setLabelsCache(prev => {
        const newCache: LabelsCache = new Map(prev);
        newCache.set(optionId, optionLabel);
        return newCache;
      });
    }

    onChange?.(newSelected);
  };

  const toggleSelectAll = () => {
    if (options.length === 0) return;

    const optionIds = options.map(opt => opt.id);
    const allSelected = optionIds.every(id => selectedValues.includes(id));

    if (allSelected) {
      // Desmarcar todos os da lista atual
      onChange?.(selectedValues.filter(id => !optionIds.includes(id)));
    } else {
      // Marcar todos os da lista atual
      const newSelected = [...new Set([...selectedValues, ...optionIds])];

      // Atualizar cache com todos os labels
      setLabelsCache(prev => {
        const newCache: LabelsCache = new Map(prev);
        options.forEach(opt => {
          if (opt.id && opt.label) {
            newCache.set(opt.id, opt.label);
          }
        });
        return newCache;
      });

      onChange?.(newSelected);
    }
  };

  const handleClear = (e: ReactMouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onChange?.([]);
  };

  const selectedCount = selectedValues.length;

  // Gerar texto de exibição
  const getDisplayText = (): string => {
    if (selectedCount === 0) return placeholder;
    if (selectedCount === 1) {
      const cachedLabel = labelsCache.get(selectedValues[0]);
      return cachedLabel || '1 selecionado';
    }
    return `${selectedCount} selecionados`;
  };

  const allCurrentSelected = options.length > 0 &&
    options.every(opt => selectedValues.includes(opt.id));

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
        <label style={{ fontSize: '12px', fontWeight: '600' }}>
          {label}
        </label>
        {selectedCount > 0 && (
          <button
            onClick={handleClear}
            style={{
              background: 'none',
              border: 'none',
              padding: '2px 6px',
              cursor: 'pointer',
              fontSize: '11px',
              color: 'var(--muted)',
              display: 'flex',
              alignItems: 'center',
              gap: '2px'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-primary)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--muted)'; }}
          >
            <X size={12} />
            Limpar
          </button>
        )}
      </div>
      {/* P3-017: semantic <button> with aria-expanded so the trigger is
          keyboard-reachable and screen-readers announce open state. */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={label}
        className={hasActiveFilter ? 'multiselect-dropdown--has-filter' : ''}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: '8px 12px',
          border: hasActiveFilter ? '2px solid transparent' : '1px solid var(--panel-border)',
          borderRadius: '6px',
          background: 'white',
          cursor: 'pointer',
          minHeight: '36px',
          font: 'inherit',
          textAlign: 'left',
          color: 'inherit',
        }}
      >
        <span style={{ fontSize: '14px' }}>{getDisplayText()}</span>
        <ChevronDown size={16} style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          marginTop: '4px',
          background: 'white',
          border: '1px solid var(--panel-border)',
          borderRadius: '4px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          maxHeight: '350px',
          zIndex: 1000
        }}>
          {/* Search input */}
          <div style={{ padding: '8px', borderBottom: '1px solid #f3f4f6' }}>
            <input
              ref={inputRef}
              type="text"
              placeholder="Digite para buscar..."
              value={searchTerm}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                padding: '6px 8px',
                border: '1px solid var(--panel-border)',
                borderRadius: '4px',
                fontSize: '14px',
                outline: 'none'
              }}
            />
          </div>

          {/* Loading */}
          {loading && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '20px',
              color: 'var(--muted)'
            }}>
              <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
              Buscando...
            </div>
          )}

          {/* Instrução inicial para minChars > 0 */}
          {!loading && !hasSearched && minChars > 0 && (
            <div style={{ padding: '12px', textAlign: 'center', color: 'var(--muted)', fontSize: '13px' }}>
              Digite pelo menos {minChars} caracteres para buscar
            </div>
          )}

          {/* Select All (apenas quando tem resultados) */}
          {!loading && options.length > 0 && (
            <div
              onClick={toggleSelectAll}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                cursor: 'pointer',
                background: '#f9fafb',
                borderBottom: '2px solid var(--panel-border)',
                fontWeight: '600'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#f9fafb'; }}
            >
              <input
                type="checkbox"
                checked={allCurrentSelected}
                onChange={() => {}}
                style={{ cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px' }}>
                {allCurrentSelected ? 'Desmarcar todos' : 'Selecionar todos'}
              </span>
              <span style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--muted)' }}>
                ({options.length} resultados)
              </span>
            </div>
          )}

          {/* Options list */}
          {!loading && options.length > 0 && (
            <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
              {options.map(option => (
                <div
                  key={option.id}
                  onClick={() => toggleOption(option.id, option.label)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 12px',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
                >
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(option.id)}
                    onChange={() => {}}
                    style={{ cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '14px' }}>{option.label}</span>
                </div>
              ))}
            </div>
          )}

          {/* Nenhum resultado */}
          {!loading && hasSearched && options.length === 0 && (
            <div style={{ padding: '12px', textAlign: 'center', color: 'var(--muted)', fontSize: '14px' }}>
              Nenhum resultado encontrado
            </div>
          )}

          {/* Itens selecionados que não estão na lista atual */}
          {selectedCount > 0 && !loading && (
            <div style={{
              borderTop: '1px solid #e5e7eb',
              padding: '8px',
              background: '#f9fafb'
            }}>
              <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
                Selecionados ({selectedCount}):
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {selectedValues.slice(0, 10).map(id => {
                  const cachedLabel = labelsCache.get(id) || id.substring(0, 8) + '...';
                  return (
                    <span
                      key={id}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleOption(id, null);
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '2px 6px',
                        background: 'var(--color-primary)',
                        color: 'white',
                        borderRadius: '4px',
                        fontSize: '11px',
                        cursor: 'pointer'
                      }}
                    >
                      {cachedLabel}
                      <X size={10} />
                    </span>
                  );
                })}
                {selectedCount > 10 && (
                  <span style={{ fontSize: '11px', color: 'var(--muted)', alignSelf: 'center' }}>
                    +{selectedCount - 10} mais
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AsyncMultiSelectDropdown;
